import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12 } from "styled-components";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { type Mode } from "~/features/ui/get-theme-style";
import { HighlightsHighlight, type HighlightsHighlightViewProps } from "~/features/blocks/inventory/highlights/highlights-highlight";
import { RichText } from "~/features/ui/rich-text";
import { padNumber, takeFirst } from "~/features/app/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { isLinkValid } from "~/features/ui/link/utils";
import { Media } from "~/features/ui/media";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HighlightsBlockViewProps<TItem = Omit<HighlightsHighlightViewProps, "count" | "horizontal">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  footerLogo?: React.ReactNode;
  footerTitle?: string | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasCounters?: boolean | null;
  hasFooter?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  highlights?: TItem[] | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode?: string | null;
  renderHighlight: (item: TItem, index: number, count: string | undefined, horizontal: boolean) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function HighlightsBlockView<TItem = Omit<HighlightsHighlightViewProps, "count" | "horizontal">>({
  anchor,
  footerLogo,
  footerTitle,
  getItemKey,
  hasCounters = false,
  hasFooter,
  hasTopDivider = true,
  headline,
  highlights = [],
  link: _link,
  mode = "light",
  renderHighlight,
  sectionProps,
  text,
  theme = "white"
}: HighlightsBlockViewProps<TItem>) {
  const link = takeFirst(_link);
  const icon = getPlainLinkIcon(link);
  const items = highlights ?? [];
  const hasIntro = Boolean(headline || text || isLinkValid(link));
  // v3: white theme renders true white (was hardcoded grey); grey theme untouched.
  const surface = theme === "grey" ? {
    "backgroundColor": "var(--bg-secondary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  };
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "overflow": "hidden",
    "color": "var(--text-primary)"
  }, surface, hasTopDivider && {
    "borderColor": "var(--stroke-primary)",
    "@media (min-width: 1024px)": {
      "borderTopWidth": "1px"
    }
  }]}>
      <_StyledWrap>
        {hasIntro ? <_StyledGrid $_css2={[{
        "position": "relative",
        "zIndex": "10",
        "alignItems": "center",
        "> :not([hidden]) ~ :not([hidden])": {
          "--tw-divide-x-reverse": "0",
          "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
          "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
          "borderColor": "var(--stroke-primary)"
        }
      }, surface]}>
            <_StyledDiv>
              {headline && <_StyledHeadline type="h2" text={headline} />}
              {text}
              {hasFooter ? <_StyledDiv2>
                  <_StyledDiv3>{footerLogo}</_StyledDiv3>
                  <_StyledSpan>{footerTitle}</_StyledSpan>
                </_StyledDiv2> : isLinkValid(link) && <_StyledDiv4>
                    <Button as={Link} {...link} variant="secondary" icon={icon} />
                  </_StyledDiv4>}
            </_StyledDiv>
            <_StyledDiv5 $_css3={[{
          "gridColumn": "1 / -1",
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))",
            "borderColor": "var(--stroke-primary)"
          },
          "padding": "var(--spacing-md)",
          "@media (min-width: 1024px)": {
            "gridColumn": "span 6 / span 6",
            "padding": "var(--spacing-xl)"
          }
        }]}>
              {items.map((item, idx) => <div key={getItemKey?.(item, idx) ?? idx}>
                  {renderHighlight(item, idx, hasCounters ? padNumber(idx + 1) : undefined, false)}
                </div>)}
            </_StyledDiv5>
          </_StyledGrid> : <_StyledGrid2 $_css4={[{
        "position": "relative",
        "zIndex": "10",
        "marginLeft": "-0.1rem",
        "marginRight": "-0.1rem",
        "width": "calc(100% + 2px)",
        "gap": "var(--spacing-md)",
        "borderLeftWidth": "1px",
        "borderRightWidth": "1px",
        "borderColor": "var(--stroke-primary)",
        "paddingLeft": "var(--spacing-lg)",
        "paddingRight": "var(--spacing-lg)",
        "@media (min-width: 1024px)": {
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
          },
          "paddingLeft": "var(--spacing-xl)",
          "paddingRight": "var(--spacing-xl)"
        }
      }]}>
            {items.map((item, idx) => <_StyledDiv6 key={getItemKey?.(item, idx) ?? idx} $_css5={[{
          "gridColumn": "1 / -1",
          "@media (min-width: 1024px)": {
            "gridColumn": "span 4 / span 4"
          }
        }]}>
                {renderHighlight(item, idx, padNumber(idx + 1), true)}
              </_StyledDiv6>)}
          </_StyledGrid2>}
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-ylidy-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-ylidy-1"
})({
  "borderWidth": "0px",
  "borderBottomWidth": "1px",
  "borderColor": "var(--stroke-primary)"
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-ylidy-2"
})`${p => p.$_css2}`;
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-ylidy-3"
})({
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "justifyContent": "center",
  "rowGap": "var(--spacing-md)",
  "borderBottomWidth": "1px",
  "borderColor": "var(--stroke-primary)",
  "padding": "var(--spacing-xl)",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6",
    "rowGap": "var(--spacing-lg)",
    "borderWidth": "0px",
    "padding": "var(--spacing-3xl)"
  }
});
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-ylidy-4"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled6("div").withConfig({
  componentId: "sc-sf-ylidy-5"
})({
  "marginTop": "auto",
  "display": "flex",
  "alignItems": "center",
  "columnGap": "var(--spacing-lg)"
});
var _StyledDiv3 = _styled7("div").withConfig({
  componentId: "sc-sf-ylidy-6"
})({
  "position": "relative",
  "height": "8rem",
  "width": "8rem"
});
var _StyledSpan = _styled8("span").withConfig({
  componentId: "sc-sf-ylidy-7"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled9("div").withConfig({
  componentId: "sc-sf-ylidy-8"
})({
  "marginTop": "auto"
});
var _StyledDiv5 = _styled10("div").withConfig({
  componentId: "sc-sf-ylidy-9"
})`${p => p.$_css3}`;
var _StyledGrid2 = _styled11(Grid).withConfig({
  componentId: "sc-sf-ylidy-10"
})`${p => p.$_css4}`;
var _StyledDiv6 = /*#__PURE__*/_styled12("div").withConfig({
  displayName: "highlights___StyledDiv6",
  componentId: "sc-plvoq9-0"
})(["", ""], p => p.$_css5);